import type { StaticImageData } from "next/image";

import heroMeditation from "@assets/images/life/hero-meditation.webp";
import kitchenFull from "@assets/images/life/kitchen-full.webp";
import spotDogRug from "@assets/images/life/spot-dog-rug.webp";
import dinnerFull from "@assets/images/life/dinner-full.webp";
import sleepingCoupleFull from "@assets/images/life/sleeping-couple-full.webp";
import proofQuietWfh from "@assets/images/life/proof-7am-quiet-wfh.webp";
import proofCleanAirBagTrash from "@assets/images/life/proof-7am-clean-air-bag-trash-videostill.webp";
import proofSchedulingPhone from "@assets/images/life/proof-7am-scheduling-phone.webp";
import proofVoiceGestureKitchen from "@assets/images/life/proof-830am-voice-gesture-kitchen-fpo.webp";
import proofSpotCleaningDirt from "@assets/images/life/proof-830am-spot-cleaning-dirt-fpo.webp";
import proofAllTerrain from "@assets/images/life/proof-130pm-all-terrain-bedroom-rug.webp";
import proofTangleFree from "@assets/images/life/proof-130pm-tangle-free-shiba-fpo.webp";
import proofElegantDesign from "@assets/images/life/proof-7pm-elegant-design-brick-alt.webp";
import proofSurfaceDetection from "@assets/images/life/proof-7pm-surface-detection-rug-fpo.webp";
import proofSelfCleaningMop from "@assets/images/life/proof-7pm-self-cleaning-mop-pawprints-fpo.webp";
import proofPrivacyKidsRoom from "@assets/images/life/proof-11pm-privacy-kids-room.webp";
import privatePopcorn from "@assets/images/life/private-popcorn.webp";

import { type LifeVignetteId } from "@/views/life-with-matic/lifeContent";
import { config } from "@/config";

export type LifeVisual = {
  image: StaticImageData;
  alt: string;
  video?: string;
  /**
   * Horizontal `object-position` for the crop. The proof visual is 4:5 from
   * tablet up and 1:1 inline on mobile, so a 16:9 clip loses over half its
   * width and any off-centre subject drifts further than it does in the
   * source. Omitted where the subject already sits on the centre line.
   */
  focalX?: string;
};

// Full-bleed photo behind each vignette's immersive moment.
export const LIFE_MOMENT_VISUALS: Record<LifeVignetteId, LifeVisual> = {
  "wake-up-ahead": {
    image: heroMeditation,
    alt: "A person meditating by a floor-to-ceiling window while Matic rests nearby",
  },
  "rushing-out": {
    image: kitchenFull,
    alt: "A family rushing through breakfast in a busy kitchen",
  },
  "come-home": {
    image: spotDogRug,
    alt: "A dog lounging on its bed while Matic crosses the rug",
    // Matic far left, dog right of centre — a phone crop can't hold both, so
    // it holds the dog, head included.
    focalX: "70%",
  },
  "time-to-eat": {
    image: dinnerFull,
    alt: "Dinner cooking on the stove, seen from above, with Matic close by",
  },
  "trustworthy-help": {
    image: sleepingCoupleFull,
    alt: "A couple sharing popcorn on the couch while Matic waits by the table",
  },
};

// One visual per proof point, in the same order as the vignette's proofPoints.
export const LIFE_PROOF_VISUALS: Record<LifeVignetteId, readonly LifeVisual[]> =
  {
    "wake-up-ahead": [
      {
        image: proofQuietWfh,
        alt: "Matic cleaning quietly beside a sunlit window while someone works",
        video: `${config.media.baseUrl}/videos/life-quiet.mp4`,
      },
      {
        image: proofCleanAirBagTrash,
        alt: "A person replacing Matic’s sealed HEPA bag over a trash can",
        video: `${config.media.baseUrl}/videos/life-cleaner-air.mp4`,
      },
    ],
    "rushing-out": [
      {
        // TODO(agency): final voice-and-gesture visual should show a person
        // directing Matic to clean a specific kitchen mess; current still is FPO.
        image: proofVoiceGestureKitchen,
        alt: "Two hands marking a spot to clean on the map in the Matic app",
        video: `${config.media.baseUrl}/videos/life-clean-on-command.mp4`,
        focalX: "64%",
      },
      {
        // TODO(agency): final spot-cleaning visual should show targeted mopping
        // or vacuuming; current still is FPO and no clean source clip is in-repo.
        image: proofSpotCleaningDirt,
        alt: "Matic spot-cleaning a patch of dirt on the floor",
        video: `${config.media.baseUrl}/videos/life-spot-cleaning.mp4`,
      },
      {
        image: proofSchedulingPhone,
        alt: "A hand holding a phone with Matic's cleaning schedule on screen",
        video: `${config.media.baseUrl}/videos/life-easy-scheduling.mp4`,
      },
    ],
    "come-home": [
      // TODO(agency): final asset — tangle-free visual is FPO.
      {
        image: proofTangleFree,
        alt: "A woman relaxing with her dog while Matic handles the pet hair",
        video: `${config.media.baseUrl}/videos/life-tangle-free.mp4`,
      },
      {
        image: proofAllTerrain,
        alt: "Matic rolling over the edge of a bedroom rug",
        video: `${config.media.baseUrl}/videos/life-all-terrain.mp4`,
      },
    ],
    "time-to-eat": [
      // TODO(agency): final asset — surface-detection visual is FPO.
      {
        image: proofSurfaceDetection,
        alt: "Matic moving from hardwood onto a living-room rug",
        video: `${config.media.baseUrl}/videos/auto-toggles-between_mopping-and_vacuuming-video-v1.mp4`,
      },
      // TODO(agency): final asset — self-cleaning-mop visual is FPO.
      {
        image: proofSelfCleaningMop,
        alt: "Matic mopping paw prints off a hardwood floor",
        video: `${config.media.baseUrl}/videos/life-self-cleaning-mop.mp4`,
      },
    ],
    "trustworthy-help": [
      {
        image: proofPrivacyKidsRoom,
        alt: "Kids playing in their room while Matic cleans the rug",
        video: `${config.media.baseUrl}/videos/life-privacy.mp4`,
        // Centred, the crop starts past the girl on the left and drops her.
        focalX: "25%",
      },
      {
        image: proofElegantDesign,
        alt: "Matic parked beside a bookshelf in a cozy living room",
        video: `${config.media.baseUrl}/videos/life-elegant-design.mp4`,
      },
      // TODO(agency): final asset — design calls for Matic's screen during a
      // software update; no asset exists yet, evening still stands in.
      {
        image: privatePopcorn,
        alt: "Someone working at a laptop while Matic sits on the rug beside them",
        video: `${config.media.baseUrl}/videos/life-built-to-learn.mp4`,
        /* The frame spans Matic on the far left to the person on the right —
           wider than the 4:5 crop can hold. Biased onto the laptop so the
           software story reads, with Matic still at the left edge. */
        focalX: "25%",
      },
    ],
  };

export type LifeMomentSurface =
  "olive" | "rose" | "butter" | "maroon" | "black";

export type LifeMomentTheme = {
  surface: LifeMomentSurface;
  align: "start" | "end";
};

// Per-vignette theming of the expanded narrative card (Figma: each time-of-day
// moment tints the card a different brand color and places it left or right).
export const LIFE_MOMENT_THEMES: Record<LifeVignetteId, LifeMomentTheme> = {
  "wake-up-ahead": { surface: "olive", align: "end" },
  "rushing-out": { surface: "rose", align: "end" },
  "come-home": { surface: "butter", align: "end" },
  "time-to-eat": { surface: "maroon", align: "end" },
  "trustworthy-help": { surface: "black", align: "end" },
};
